import { Injectable } from '@angular/core';
import { Urls } from '@core/config/urls';
import { BaseEntity } from '@smartera/core';
import { PaginationResult } from '@smartera/core';
import { BaseApiService, LegacyBaseApiService } from '@smartera/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Application } from '../../multi-dashboard/models/application';
import { ReportDemandDetail, ReportDemandDetailCreateDTO } from '../models/report-demand/report-demand';
import { UserWidget, UserWidgetView } from '@feature/smart-reports-core/models/user-widget/user-widget';

@Injectable({
	providedIn: 'root',
})
export class UserWidgetsService extends LegacyBaseApiService<any> {
	override get apiUrl() {
		return `${Urls.BASE_URL.toString()}/analytic/report/user-widgets`;
	}

	public override findById(objectId: string): Observable<UserWidgetView> {
		return super.findById(objectId).pipe(map(this.prepareContent));
	}

	public override findPageableObjects(
		pageNumber: number,
		pageInSize: number,
		sort: any,
		searchObject: any
	): Observable<PaginationResult<UserWidgetView>> {
		return super.findPageableObjects(pageNumber, pageInSize, sort, searchObject).pipe(
			map((result) => {
				result.content = result.content.map((value) => this.prepareContent(value));
				return result;
			})
		);
	}

	searchByUserPermission(
		pageNumber: number,
		pageInSize: number,
		sort: any,
		searchObject: any
	): Observable<PaginationResult<UserWidgetView>> {
		const url = this.apiUrl + '/search-by-user-permission?page=' + pageNumber + '&size=' + pageInSize + '&sort=' + sort;
		return this._restClientService.post(url, JSON.stringify(searchObject)).pipe(
			map((result: PaginationResult<any>) => {
				result.content = result.content.map((value) => this.prepareContent(value));
				return result;
			}),
			catchError(this.handleError)
		);
	}

	override createObject(object: object): Observable<UserWidget> {
		return super.createObject(object).pipe(map(this.prepareContent));
	}

	override updateObject(id: string, object: object): Observable<UserWidget> {
		return super.updateObject(id, object).pipe(map(this.prepareContent));
	}

	private prepareContent(item: UserWidget): UserWidgetView {
		const data = JSON.parse(item.body.data) || {};
		item.body.data = JSON.parse(item.body.data);
		return {
			...item,
			title: data.title,
			valid: data.valid,
			type: data.type,
			source: data.source,
		};
	}

	public createCronExpression(resultId, data) {
		return this._restClientService.put(this.apiUrl + '/' + resultId, JSON.stringify(data));
	}
}

@Injectable({
	providedIn: 'root',
})
export class UserWidgetsSearchByPermissionService extends UserWidgetsService {
	public findPageableObjects(
		pageNumber: number,
		pageInSize: number,
		sort: any,
		searchObject: any
	): Observable<PaginationResult<UserWidgetView>> {
		return super.searchByUserPermission(pageNumber, pageInSize, sort, searchObject);
	}
}

@Injectable({
	providedIn: 'root',
})
export class UserWidgetsPermissionService extends BaseApiService<any> {
	reportId: string;

	override get apiUrl() {
		return `${Urls.BASE_URL.toString()}/analytic/report/user-widgets/` + this.reportId + `/permissions`;
	}

	createAllPermissions(model) {
		const url = this.apiUrl + `/create-all`;
		return this._restClientService.post(url, model);
	}

	deleteAllPermissions(model) {
		const url = `${this.apiUrl}/delete-all`;
		return this._restClientService.delete(url, { body: model });
	}
}

@Injectable({
	providedIn: 'root',
})
export class UserWidgetsPermissionDemandService extends BaseApiService<any> {
	reportId: string;

	override get apiUrl() {
		return `${Urls.BASE_URL.toString()}/analytic/report/user-widgets/permission-demands`;
	}

	sendToApprove(masterId) {
		return this._restClientService.put(`${this.apiUrl}/${masterId}/send-to-approve`, null);
	}

	approve(masterId) {
		return this._restClientService.put(`${this.apiUrl}/${masterId}/approve`, null);
	}

	cancel(masterId) {
		return this._restClientService.put(`${this.apiUrl}/${masterId}/cancel`, null);
	}

	reject(masterId) {
		return this._restClientService.put(`${this.apiUrl}/${masterId}/reject`, null);
	}
}

@Injectable({
	providedIn: 'root',
})
export class UserWidgetsPermissionDemandDetailService extends BaseApiService<any> {
	demandId: string;

	override get apiUrl() {
		return `${Urls.BASE_URL.toString()}/analytic/report/user-widgets/permission-demands/` + this.demandId + `/details`;
	}

	createAll(model: ReportDemandDetailCreateDTO): Observable<ReportDemandDetail[]> {
		return this._restClientService.post(`${this.apiUrl}/create-all`, JSON.stringify(model));
	}
}
export interface UserWidgetPermission extends BaseEntity {
	userWidgetId: string;
	userProfileId: string;
	userWidgetName: string;
}
